import { lazy, type ReactElement } from 'react';
import { Navigate } from 'react-router';

const L = <T extends { default: React.ComponentType<never> }>(f: () => Promise<T>) => lazy(f as never);

// Lazy-loaded screens (code-split per feature so the first paint stays light on 2G/3G — Guide §2.3).
export const P = {
  PinLock: L(() => import('./features/auth/PinLock')),
  Legal: L(() => import('./features/auth/Legal')),
  SetupFlow: L(() => import('./features/auth/SetupFlow')),
  ForcePin: L(() => import('./features/auth/ForcePin')),
  Welcome: L(() => import('./features/auth/Welcome')),
  Signup: L(() => import('./features/auth/Signup')),
  Login: L(() => import('./features/auth/Login')),
  Recover: L(() => import('./features/auth/Recover')),
  Home: L(() => import('./features/home/Home')),
  Baki: L(() => import('./features/baki/Baki')),
  Customers: L(() => import('./features/baki/Customers')),
  CustomerDetail: L(() => import('./features/baki/CustomerDetail')),
  CustomerForm: L(() => import('./features/baki/CustomerForm')),
  Stock: L(() => import('./features/stock/Stock')),
  ProductDetail: L(() => import('./features/stock/ProductDetail')),
  ProductForm: L(() => import('./features/stock/ProductForm')),
  More: L(() => import('./features/more/More')),
  NewSale: L(() => import('./features/sale/NewSale')),
  Invoices: L(() => import('./features/invoices/Invoices')),
  InvoiceView: L(() => import('./features/invoices/InvoiceView')),
  ReturnPage: L(() => import('./features/invoices/ReturnPage')),
  Khata: L(() => import('./features/khata/Khata')),
  Expenses: L(() => import('./features/expenses/Expenses')),
  Vendors: L(() => import('./features/vendors/Vendors')),
  VendorDetail: L(() => import('./features/vendors/VendorDetail')),
  PurchaseNew: L(() => import('./features/vendors/PurchaseNew')),
  Reports: L(() => import('./features/reports/Reports')),
  ReportView: L(() => import('./features/reports/ReportView')),
  Profit: L(() => import('./features/profit/Profit')),
  Investment: L(() => import('./features/stock/Investment')),
  Notes: L(() => import('./features/notes/Notes')),
  Recycle: L(() => import('./features/recycle/Recycle')),
  Review: L(() => import('./features/sync/Review')),
  SyncProblems: L(() => import('./features/sync/SyncProblems')),
  Sms: L(() => import('./features/sms/Sms')),
  Insights: L(() => import('./features/insights/Insights')),
  Assistant: L(() => import('./features/assistant/Assistant')),
  Zakat: L(() => import('./features/zakat/Zakat')),
  ImportExport: L(() => import('./features/importexport/ImportExport')),
  Help: L(() => import('./features/support/Help')),
  TicketDetail: L(() => import('./features/support/TicketDetail')),
  Notifications: L(() => import('./features/notifications/Notifications')),
  Settings: L(() => import('./features/settings/Settings')),
  ShopProfile: L(() => import('./features/settings/ShopProfile')),
  Preferences: L(() => import('./features/settings/Preferences')),
  Accounts: L(() => import('./features/settings/Accounts')),
  Staff: L(() => import('./features/settings/Staff')),
  Devices: L(() => import('./features/settings/Devices')),
  Plan: L(() => import('./features/settings/Plan')),
  BillingResult: L(() => import('./features/settings/BillingResult')),
  Account: L(() => import('./features/settings/Account')),
  PinChange: L(() => import('./features/settings/PinChange')),
  Printer: L(() => import('./features/settings/Printer')),
  Data: L(() => import('./features/settings/Data')),
  NewShop: L(() => import('./features/settings/NewShop')),
  Tab2Hub: L(() => import('./features/hub/Tab2Hub')),
  Tab4Hub: L(() => import('./features/hub/Tab4Hub')),
  ModuleList: L(() => import('./features/modules/ModuleList')),
  ModuleForm: L(() => import('./features/modules/ModuleForm')),
  TxnForm: L(() => import('./features/mfs/TxnForm')),
  EntryForm: L(() => import('./features/personal/EntryForm')),
  Loans: L(() => import('./features/personal/Loans')),
  LoanDetail: L(() => import('./features/personal/LoanDetail')),
  AccountDetail: L(() => import('./features/personal/AccountDetail')),
  NewOrder: L(() => import('./features/restaurant/NewOrder')),
  OrderScreen: L(() => import('./features/restaurant/OrderScreen')),
} as const;

type R = { path: string; element: ReactElement };
const el = (C: React.ComponentType): ReactElement => <C />;

export const publicRoutes: R[] = [
  { path: '/welcome', element: el(P.Welcome) },
  { path: '/signup', element: el(P.Signup) },
  { path: '/login', element: el(P.Login) },
  { path: '/recover', element: el(P.Recover) },
];
const home = <Navigate to="/" replace />;

/** Inside the main shell: the four tabs plus every page that lives inside "আরও" (their Bottom Bar keeps "আরও" active). */
export const mainTabs: R[] = [
  { path: '/', element: el(P.Home) },
  { path: '/baki', element: el(P.Baki) },
  { path: '/stock', element: el(P.Stock) },
  { path: '/more', element: el(P.More) },
  { path: '/cash', element: el(P.Tab2Hub) },
  { path: '/tab4', element: el(P.Tab4Hub) },
  { path: '/m/:entity', element: el(P.ModuleList) },
  { path: '/customers', element: el(P.Customers) },
  { path: '/invoices', element: el(P.Invoices) },
  { path: '/khata', element: el(P.Khata) },
  { path: '/expenses', element: el(P.Expenses) },
  { path: '/expenses/new', element: el(P.Expenses) }, // Home quick action "খরচ": the list with the entry sheet open
  { path: '/vendors', element: el(P.Vendors) },
  { path: '/stock/investment', element: el(P.Investment) },
  { path: '/profit', element: el(P.Profit) },
  { path: '/insights', element: el(P.Insights) },
  { path: '/zakat', element: el(P.Zakat) },
  { path: '/reports', element: el(P.Reports) },
  { path: '/notes', element: el(P.Notes) },
  { path: '/import', element: el(P.ImportExport) },
  { path: '/sms', element: el(P.Sms) },
  { path: '/review', element: el(P.Review) },
  { path: '/settings', element: el(P.Settings) },
  { path: '/help', element: el(P.Help) },
  { path: '/recycle', element: el(P.Recycle) },
];

/** Detail / form / task screens: Back only, no Bottom Bar (Bible 13.1). */
export const subRoutes: R[] = [
  { path: '/sale/new', element: el(P.NewSale) },
  { path: '/assistant', element: el(P.Assistant) },
  { path: '/m/:entity/new', element: el(P.ModuleForm) },
  { path: '/m/:entity/:id', element: el(P.ModuleForm) },
  { path: '/mfs/txn/new', element: el(P.TxnForm) },
  { path: '/personal/new', element: el(P.EntryForm) },
  { path: '/personal/loans', element: el(P.Loans) },
  { path: '/personal/loans/:id', element: el(P.LoanDetail) },
  { path: '/personal/accounts/:id', element: el(P.AccountDetail) },
  { path: '/orders/new', element: el(P.NewOrder) },
  { path: '/orders/:id', element: el(P.OrderScreen) },
  { path: '/baki/:id', element: el(P.CustomerDetail) },
  { path: '/customers/new', element: el(P.CustomerForm) },
  { path: '/customers/:id', element: el(P.CustomerDetail) },
  { path: '/customers/:id/edit', element: el(P.CustomerForm) },
  { path: '/stock/new', element: el(P.ProductForm) },
  { path: '/stock/:id', element: el(P.ProductDetail) },
  { path: '/stock/:id/edit', element: el(P.ProductForm) },
  { path: '/invoices/:id', element: el(P.InvoiceView) },
  { path: '/invoices/:id/return', element: el(P.ReturnPage) },
  { path: '/vendors/:id', element: el(P.VendorDetail) },
  { path: '/purchases/new', element: el(P.PurchaseNew) },
  { path: '/reports/:kind', element: el(P.ReportView) },
  { path: '/help/tickets/:id', element: el(P.TicketDetail) },
  { path: '/notifications', element: el(P.Notifications) },
  { path: '/sync-problems', element: el(P.SyncProblems) },
  { path: '/shops/new', element: el(P.NewShop) },
  { path: '/settings/shop', element: el(P.ShopProfile) },
  { path: '/settings/preferences', element: el(P.Preferences) },
  { path: '/settings/accounts', element: el(P.Accounts) },
  { path: '/settings/staff', element: el(P.Staff) },
  { path: '/settings/devices', element: el(P.Devices) },
  { path: '/settings/plan', element: el(P.Plan) },
  { path: '/settings/account', element: el(P.Account) },
  { path: '/settings/pin', element: el(P.PinChange) },
  { path: '/settings/printer', element: el(P.Printer) },
  { path: '/settings/data', element: el(P.Data) },
  { path: '/billing/result', element: el(P.BillingResult) },
];
